import type { Battle } from '@mezha/api'
import { atlasFor, sceneEvents, type SceneEvent } from './model'
import type { Playback } from './usePlayback'
import { resolveCombatArt, sourceFrame, paintFrame } from './atlas'
import { actorAt, motionAt, travelFactor, meleePosition, meleeProgress, meleeReach, duelDistance, impactHeight } from './animation'

const colors = { hit: '#ffe2c1', crit: '#ffd178', guard: '#d0e5ea', magic: '#95d9e7', heal: '#c4ed9d', status: '#f0b2a1', rest: '#e1d8b8' }
const clamp = (n: number) => Math.max(0, Math.min(1, n))
type Point = { x: number; y: number; size: number; facing: number }
type ImageBank = Map<string, HTMLImageElement>
export const atlasURL = (name: string) => `/assets/combat/${name}-poses-v01.webp`

function label(c: CanvasRenderingContext2D, text: string, x: number, y: number, color: string, size: number, max: number) {
  c.font = `600 ${size}px Georgia, serif`; c.textAlign = 'center'; c.textBaseline = 'middle'
  c.lineWidth = 4; c.strokeStyle = '#20190ee6'; c.strokeText(text, x, y, max)
  c.fillStyle = color; c.fillText(text, x, y, max)
}

function ring(c: CanvasRenderingContext2D, p: Point, color: string, alpha: number) {
  c.save(); c.strokeStyle = color; c.globalAlpha *= alpha; c.lineWidth = 2
  c.shadowBlur = 12; c.shadowColor = color
  c.beginPath(); c.ellipse(p.x, p.y - p.size * .35, p.size * .25, p.size * .38, 0, 0, Math.PI * 2); c.stroke()
  c.beginPath(); c.ellipse(p.x, p.y, p.size * .34, p.size * .08, 0, 0, Math.PI * 2); c.stroke(); c.restore()
}

function effect(c: CanvasRenderingContext2D, e: SceneEvent, source: Point, target: Point, t: number) {
  const dt = t - e.at
  const col = colors[e.tone]
  if (e.attack && dt > -.4 && dt < 0 && (e.attack.kind === 'spirit' || e.attack.family === 'bow')) {
    const p = clamp((dt + .4) / .4)
    c.save(); c.strokeStyle = col; c.lineWidth = e.attack.kind === 'spirit' ? 4 : 2
    c.shadowBlur = 12; c.shadowColor = col
    const fromY = source.y - source.size * .5
    const dx = target.x - source.x, dy = target.y - target.size * impactHeight(e.attack.attack) - fromY
    const headX = source.x + dx * p, headY = fromY + dy * p
    const tail = e.attack.family === 'bow' && e.attack.kind !== 'spirit' ? Math.max(0, p - 24 / Math.max(1, Math.hypot(dx, dy))) : 0
    c.beginPath(); c.moveTo(source.x + dx * tail, fromY + dy * tail); c.lineTo(headX, headY); c.stroke()
    if (tail > 0) {
      const angle = Math.atan2(dy, dx)
      c.beginPath(); c.moveTo(headX - Math.cos(angle - .5) * 6, headY - Math.sin(angle - .5) * 6); c.lineTo(headX, headY)
      c.lineTo(headX - Math.cos(angle + .5) * 6, headY - Math.sin(angle + .5) * 6); c.stroke()
    }
    c.restore()
  }
  if (dt < 0 || dt > .6) return
  c.save(); c.globalAlpha = 1 - dt / .6
  if (e.tone === 'heal' || e.tone === 'magic' || e.tone === 'rest' || e.tone === 'guard') ring(c, target, col, .85)
  if (e.attack && !['miss', 'dodge', 'parry'].includes(e.attack.outcome)) {
    const y = target.y - target.size * impactHeight(e.attack.attack)
    c.strokeStyle = col; c.shadowBlur = 9; c.shadowColor = col; c.lineWidth = e.tone === 'crit' ? 4 : 2
    for (let i = 0; i < 12; i++) {
      const a = i * 2.4, r = dt * (40 + i * 8)
      c.beginPath(); c.moveTo(target.x + Math.cos(a) * r, y + Math.sin(a) * r)
      c.lineTo(target.x + Math.cos(a) * (r + 8), y + Math.sin(a) * (r + 8)); c.stroke()
    }
    c.beginPath(); c.ellipse(target.x, y, target.size * .3, target.size * .13, -.6, -1, 2); c.stroke()
  }
  c.restore()
}

/** Canvas owns no game state. Caller supplies the current authoritative snapshot. */
export function drawScene(c: CanvasRenderingContext2D, w: number, h: number, b: Battle, play: Playback | null,
  time: number, reduced: boolean, images: ImageBank, targetId: string | null) {
  c.clearRect(0, 0, w, h)
  const t = play ? (time - play.started) / 1000 : -1
  const events = play ? sceneEvents(play.round, b.skill_names) : []
  const points = new Map<string, Point>()
  const bodies = new Map<string, Point>()
  const drawn = new Map<string, Point>()
  const teams = [b.actors.filter(a => a.side === b.your_side), b.actors.filter(a => a.side !== b.your_side)]
  const maxSide = Math.max(...teams.map(a => a.length), 1)
  // Duel resources already identify both fighters; teams still need names under each figure.
  const ground = Math.min(h * .88, h - (maxSide > 1 ? 38 : 10))
  const laneHeight = (ground - 12) / maxSide
  const size = Math.max(1, Math.min(ground - 8, w * (maxSide > 1 ? .27 : .48), maxSide > 1 ? laneHeight * .76 : 440))
  teams.forEach((actors, side) => actors.forEach((a, i) => {
    const x = w * (side === 0 ? .27 : .73)
    const y = ground - (actors.length - 1 - i) * laneHeight
    const context = play ? actorAt(play.before?.find(old => old.id === a.id) ?? a, a, events, t) : a
    const art = resolveCombatArt(context)
    const extent = art?.fitExtent
    const fitted = extent ? Math.min(size, (y - 8) / extent.up, (h - y - 4) / Math.max(.01, extent.down), (x - 8) / (side === 0 ? extent.left : extent.right), (w - x - 8) / (side === 0 ? extent.right : extent.left)) : size
    const actorSize = Math.max(1, fitted)
    points.set(a.id, { x, y, size: actorSize, facing: side === 0 ? 1 : -1 })
    bodies.set(a.id, { ...points.get(a.id)!, size: actorSize * (art ? art.scale * (art.heightRatio ?? .8) : atlasFor(context) === 'lugar' ? .75 : 1) })
  }))
  if (maxSide === 1 && b.actors.length === 2 && b.actors.every(a => !['bow', 'staff'].includes(a.weapon_family ?? ''))) {
    const [left, right] = teams.map(side => side[0])
    const reach = Math.max(...b.actors.map(a => meleeReach(a.weapon_family)))
    const distance = duelDistance(bodies.get(left.id)!, bodies.get(right.id)!, reach)
    for (const [actor, sign] of [[left, -1], [right, 1]] as const) {
      points.get(actor.id)!.x = w / 2 + sign * distance / 2
      bodies.get(actor.id)!.x = w / 2 + sign * distance / 2
    }
  }
  for (const a of [...b.actors].sort((a, b) => points.get(a.id)!.y - points.get(b.id)!.y)) {
    const base = points.get(a.id)!
    const context = play ? actorAt(play.before?.find(old => old.id === a.id) ?? a, a, events, t) : a
    const art = resolveCombatArt(context)
    const atlas = atlasFor(context), img = images.get(art?.url ?? (atlas ? atlasURL(atlas) : ''))
    const magic = events.some(e => e.target === a.id && (e.tone === 'magic' || e.tone === 'heal') && t > e.at - .2 && t < e.at + .8)
    const p = { ...base }
    let frame = 0
    const roundActor = play?.round.actors.find(r => r.id === a.id)
    const out = t >= 0 && t < 2.05 ? roundActor ? roundActor.hp_before <= 0 : false : a.out
    const motion = motionAt({ ...context, out }, events, t, reduced)
    const { outgoing, incoming } = motion
    if (out && atlas === 'lugar') frame = 5
    if (!out && outgoing) {
      const dt = t - outgoing.at
      const ranged = outgoing.attack?.kind === 'spirit' || outgoing.attack?.family === 'bow'
      const destination = bodies.get(outgoing.target) ?? base
      const progress = meleeProgress(dt)
      if (!ranged) {
        const partner = events.find(e => e.actor === outgoing.target && e.target === a.id && e.attack && Math.abs(e.at - outgoing.at) < .2 && e.attack.kind !== 'spirit' && e.attack.family !== 'bow')
        const other = play?.before?.find(old => old.id === outgoing.target) ?? b.actors.find(other => other.id === outgoing.target)
        const reach = Math.max(meleeReach(outgoing.attack?.family), partner ? meleeReach(partner.attack?.family) : 0)
        // Finish the already-started trajectory; newly applied pinned must not teleport feet home.
        Object.assign(p, meleePosition(bodies.get(a.id)!, destination, progress, reach, !!partner, travelFactor(play?.before?.find(old => old.id === a.id) ?? context), other ? travelFactor(other) : 1))
        const step = Math.hypot(p.x - base.x, p.y - base.y)
        p.y -= Math.min(4, step * .08) * Math.sin(progress * Math.PI)
      }
      frame = ranged ? 4 : dt < -.13 ? 1 : atlas === 'lugar' && dt > .12 ? 3 : 2
      if (!art && atlas === 'lugar') p.y -= base.size * .16 * (dt < 0 ? Math.sin(progress * Math.PI / 2) : 1 - clamp(dt / .2))
    } else if (!out && magic) frame = atlas === 'wanderer' ? 4 : 0
    else if (!out && incoming) {
      const a = incoming.attack!
      frame = atlas === 'wanderer' && (a.blocked || a.guarded || a.outcome === 'parry') ? 3 : a.applied > 0 ? atlas === 'lugar' ? 4 : 5 : 0
      if (a.outcome === 'dodge' || a.applied > 0) p.x -= base.facing * 12 * Math.sin(clamp((t - incoming.at) / .32) * Math.PI)
    }
    if (!play && !reduced && !out) p.y += Math.sin(time / 650 + base.x) * 1.1
    // Recoil can coexist with an outgoing attack from the same simultaneous round.
    if (outgoing) p.x -= base.facing * 8 * motion.recoil
    if (art?.extent) {
      const s = base.size * art.scale, e = art.extent
      p.x = Math.max(4 + s * (base.facing > 0 ? e.left : e.right), Math.min(w - 4 - s * (base.facing > 0 ? e.right : e.left), p.x))
      p.y = Math.max(4 + s * e.up, Math.min(h - 4 - s * e.down, p.y))
    }
    const body = { ...p, size: bodies.get(a.id)!.size }
    drawn.set(a.id, body)
    c.save(); c.globalAlpha = out ? .45 : 1
    c.fillStyle = '#0f120c55'; c.beginPath(); c.ellipse(p.x, p.y, body.size * .22, body.size * .045, 0, 0, Math.PI * 2); c.fill()
    if (a.id === targetId && maxSide > 1) {
      c.strokeStyle = '#c9af7c99'; c.lineWidth = 2
      const half = body.size * .22, edge = Math.min(12, half * .3)
      c.beginPath(); c.moveTo(p.x - half, p.y - 3); c.lineTo(p.x - half, p.y + 3); c.lineTo(p.x - half + edge, p.y + 3)
      c.moveTo(p.x + half - edge, p.y + 3); c.lineTo(p.x + half, p.y + 3); c.lineTo(p.x + half, p.y - 3); c.stroke()
    }
    if (img && img.complete && img.naturalWidth > 0) {
      if (art) {
        // Breathing is the continuous movement above, never a second painted face.
        const f = sourceFrame(art, motion.pose, img.naturalWidth, img.naturalHeight)
        const pixelScale = base.size * art.scale / (img.naturalHeight / art.rows)
        paintFrame(c, img, f, p.x, p.y, pixelScale, base.facing * art.facing)
      } else if (atlas) {
        const s = size * (atlas === 'lugar' ? .75 : 1)
        const flip = atlas === 'lugar' ? -base.facing : base.facing
        c.save(); c.translate(p.x, p.y); c.scale(flip, 1)
        c.drawImage(img, frame % 3 * 512, Math.floor(frame / 3) * 512, 512, 512, -s / 2, -s * (atlas === 'wanderer' ? .955 : frame < 3 ? .91 : .82), s, s)
        c.restore()
      }
    } else {
      c.fillStyle = '#252b24'; c.strokeStyle = '#a98b56'; c.lineWidth = 2
      c.beginPath(); c.ellipse(p.x, p.y - size * .4, size * .2, size * .27, 0, 0, Math.PI * 2); c.fill(); c.stroke()
      label(c, a.name.slice(0, 1), p.x, p.y - size * .4, '#e4cc9a', size * .14, size * .4)
    }
    if (maxSide > 1) {
      label(c, a.name, base.x, base.y + 14, '#f9e8c6', w < 600 ? 12 : 14, w * .4 / maxSide)
      const hp = t >= 0 && t < 2.05 && roundActor ? roundActor.hp_before : a.hp
      label(c, `${hp}/${a.hp_max}`, base.x, base.y + 31, '#e3dab8', 11, size)
    }
    c.restore()
  }
  const lanes = new Map<string, number>()
  for (const e of events) {
    const source = drawn.get(e.actor), target = drawn.get(e.target)
    if (!source || !target) continue
    if (!reduced) effect(c, e, source, target, t)
    const dt = t - e.at
    if (dt < 0 || dt > .95) continue
    const lane = lanes.get(e.target) ?? 0
    lanes.set(e.target, lane + 1)
    c.save(); c.globalAlpha = Math.min(1, (.95 - dt) * 4)
    label(c, e.text, target.x, Math.max(24, target.y - target.size * .8) + lane * 19 - (reduced ? 0 : dt * 12), colors[e.tone], w < 600 ? 12 : 16, w * .44)
    c.restore()
  }
}
